import { useQuery } from '@tanstack/react-query';

import { getNamespaces } from '@/react/kubernetes/namespaces/queries/useNamespacesQuery';

import { useAddonRelease } from './useAddonRelease';

export type AddonReleaseScope =
  | 'loading'
  | 'release'
  | 'namespaceOnly'
  | 'missing';

/**
 * useAddonReleaseScope decides what an add-on detail tab can show. The add-on's
 * Helm release may not exist yet (still installing) or may have failed, so when
 * the release is absent it checks whether the add-on's namespace exists:
 *  - `release`       — the release loaded; tabs show release-scoped data.
 *  - `namespaceOnly` — no release but the namespace exists; tabs fall back to
 *    namespace-wide data so a slow or failing install stays debuggable.
 *  - `missing`       — neither release nor namespace exists; nothing to show.
 *  - `loading`       — still resolving.
 */
export function useAddonReleaseScope(
  id: string,
  options: { showResources?: boolean; refetchInterval?: number } = {}
) {
  const { query, environmentId, namespace } = useAddonRelease(id, options);

  // Only consulted when the release is absent, so it's gated on the release
  // error — a healthy release never triggers the lookup. Kept toast-free (unlike
  // useNamespacesQuery) since "namespace missing" is an expected answer here.
  const namespaceQuery = useQuery(
    ['addon-namespace-exists', environmentId, namespace],
    async () => {
      const namespaces = await getNamespaces(environmentId ?? 0);
      return namespaces.some((ns) => ns.Name === namespace);
    },
    { enabled: !!environmentId && query.isError }
  );

  const scope = resolveScope();

  return { scope, query, environmentId, namespace };

  function resolveScope(): AddonReleaseScope {
    if (query.isSuccess) {
      return 'release';
    }
    if (!query.isError) {
      return 'loading';
    }
    if (namespaceQuery.isLoading) {
      return 'loading';
    }
    return namespaceQuery.data ? 'namespaceOnly' : 'missing';
  }
}
